import { useState } from 'react';
import { IGroupedMentionWithMentioner, IUpdateMentionDto } from 'types/dashboard.types';
import { updateMention } from 'services/dashboard.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { redirectToConversation } from '../../../utils/mentionsColumn.utils';
import { NudosHoverText, NudosSelectButton } from 'components/NudosComponents';
import CommentImage from 'components/BordComentsAndObservations/components/CommentImage/CommentImage';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';

import './MentionCard.scss';

interface IMentionCardProps {
  mention: IGroupedMentionWithMentioner;
  redirectUrl: string;
  reloadData: () => Promise<void>;
  showLoader: boolean;
}

export const MentionCard = ({ mention, redirectUrl, reloadData, showLoader }: IMentionCardProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { id: mentionId, comment, mentioner, wasSeen, wasResolved, createdAt } = mention;

  const handleUpdateMention = async (body: IUpdateMentionDto) => {
    try {
      setIsLoading(true);
      await updateMention(mentionId, body);
      await reloadData();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateViewed = () => handleUpdateMention({ wasSeen: !wasSeen });
  const handleUpdateResolved = () => handleUpdateMention({ wasResolved: !wasResolved });

  if (!comment) return null;

  if (isLoading || showLoader) return <div className="mentionCard animationLoader" />;

  return (
    <div className="mentionCard">
      <div className="mentionerAndOrigin">
        <div className="titleAndOrigin">
          <div className="mentionerTitle">Te mencionó:</div>
          <div className="mentionOrigin" onClick={() => redirectToConversation(redirectUrl)}>
            Ir a los comentarios
          </div>
        </div>
        <NudosHoverText customClassName="mentionUserInfo" text={mentioner.name} charactersLimit={30} onlyIfTruncated />
      </div>

      <div className="commentContainer">
        <p className="commentTitle">Mensaje: </p>
        <div className="commentContent">
          {comment?.imageUrl && <CommentImage imageUrl={comment?.imageUrl} displayDownloadButton={false} />}
          {comment?.comment && <div className="message">{comment?.comment}</div>}
        </div>
      </div>

      <div className="mentionDate">{formatShortDateAndHour(createdAt || '')?.textWithTimezone}</div>

      <div className="mentionActions">
        <div className="mentionCardButton" onClick={handleUpdateViewed}>
          <div className="mentionCardButtonText">Marcar como leído</div>
          <NudosSelectButton
            isButtonActive={wasSeen}
            customClassName="mentionCardButton"
            componentSize="small"
            handleClickButton={handleUpdateViewed}
          />
        </div>
        <div className="mentionCardButton" onClick={handleUpdateResolved}>
          <div className="mentionCardButtonText">Marcar como resuelto</div>
          <NudosSelectButton
            isButtonActive={wasResolved}
            customClassName="mentionCardButton"
            componentSize="small"
            handleClickButton={handleUpdateResolved}
          />
        </div>
      </div>
    </div>
  );
};
